// One row for a graded auto-scorable question. Shared between MockReviewPage
// (full sitting drill-in) and PracticeReviewPage (single practice set
// drill-in) so both look and behave identically — verdict icon, MCQ option
// highlighting for correct/yours/both, trap label + why-yours-attracts +
// explanation blocks.
import React from 'react';
import { Check, X } from 'lucide-react';

import { prettyTask } from '@/lib/labels';
import { cn } from '@/lib/utils';

function optionsFor(row) {
  return row.options || (row.set_stimulus && row.set_stimulus.options) || null;
}

export default function QuestionReviewRow({ row, index }) {
  const opts = optionsFor(row);
  const isMulti = row.interaction === 'mcq_multi';
  const yourIdx = isMulti ? (row.your_answer || []) : row.your_answer;
  const keyIdx = isMulti ? (row.answer_indices || []) : row.answer_index;
  const isLegacy = !!row.legacy;
  return (
    <div className="border-t border-slate-100 px-5 py-4 first:border-t-0" data-testid={`review-q-${row.qid}`}>
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold',
            isLegacy
              ? 'bg-slate-100 text-slate-500'
              : row.correct
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-rose-100 text-rose-700'
          )}
          data-testid={`review-q-${row.qid}-verdict`}
        >
          {isLegacy ? <span className="text-[10px]">?</span> : row.correct ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Question {index + 1} · {row.set_title || prettyTask(row.set_task_type)}
          </p>
          {row.prompt && (
            <p className="mt-1 text-[15px] font-medium leading-6 text-slate-800">{row.prompt}</p>
          )}
          {opts && !isLegacy && (Array.isArray(yourIdx) || typeof yourIdx === 'number') ? (
            <ul className="mt-3 space-y-1.5">
              {opts.map((opt, i) => {
                const isKey = isMulti ? keyIdx.includes(i) : keyIdx === i;
                const isYours = isMulti ? (yourIdx || []).includes(i) : yourIdx === i;
                const cls = isKey && isYours
                  ? 'border-emerald-500 bg-emerald-50/60 text-emerald-900'
                  : isKey
                    ? 'border-emerald-500 bg-emerald-50/40 text-emerald-900'
                    : isYours
                      ? 'border-rose-400 bg-rose-50/60 text-rose-900'
                      : 'border-slate-200 bg-white text-slate-600';
                return (
                  <li key={i} className={cn('flex items-start gap-3 border-l-2 px-3 py-1.5 text-sm leading-6', cls)}>
                    <span className="text-xs font-semibold tabular-nums">{String.fromCharCode(65 + i)}.</span>
                    <span className="flex-1">{typeof opt === 'string' ? opt : (opt?.text || JSON.stringify(opt))}</span>
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider">
                      {isKey && isYours ? 'Correct · Yours' : isKey ? 'Correct' : isYours ? 'Yours' : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : opts && isLegacy ? (
            // Legacy row — we never stored the student's pick. Show every
            // option and highlight only the correct answer key, so the
            // student can still study the passage vs. the right answer.
            <ul className="mt-3 space-y-1.5" data-testid={`review-q-${row.qid}-legacy-options`}>
              {opts.map((opt, i) => {
                const isKey = isMulti ? (keyIdx || []).includes(i) : keyIdx === i;
                const cls = isKey
                  ? 'border-emerald-500 bg-emerald-50/40 text-emerald-900'
                  : 'border-slate-200 bg-white text-slate-600';
                return (
                  <li key={i} className={cn('flex items-start gap-3 border-l-2 px-3 py-1.5 text-sm leading-6', cls)}>
                    <span className="text-xs font-semibold tabular-nums">{String.fromCharCode(65 + i)}.</span>
                    <span className="flex-1">{typeof opt === 'string' ? opt : (opt?.text || JSON.stringify(opt))}</span>
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider">
                      {isKey ? 'Correct' : ''}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="mt-3 space-y-1.5 text-sm">
              {!isLegacy && row.your_answer !== undefined && (
                <p className="border-l-2 border-slate-300 bg-slate-50/60 px-3 py-1.5 text-slate-700">
                  <span className="mr-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Yours</span>
                  {String(row.your_answer ?? '(no answer)')}
                </p>
              )}
              {row.answer !== undefined && (
                <p className="border-l-2 border-emerald-500 bg-emerald-50/40 px-3 py-1.5 text-emerald-900">
                  <span className="mr-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Correct</span>
                  {String(row.answer)}
                </p>
              )}
              {row.answer_text !== undefined && (
                <p className="border-l-2 border-emerald-500 bg-emerald-50/40 px-3 py-1.5 text-emerald-900">
                  <span className="mr-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Correct</span>
                  {String(row.answer_text)}
                </p>
              )}
            </div>
          )}
          {row.trap_label && !isLegacy && (
            <p className="mt-3 border-l-2 border-amber-500 bg-amber-50/50 px-3 py-1.5 text-sm text-slate-700" data-testid={`review-q-${row.qid}-trap`}>
              <span className="mr-2 text-[10px] font-semibold uppercase tracking-wider text-amber-700">Trap</span>
              {row.trap_label}
            </p>
          )}
          {row.why_yours_attracts && !isLegacy && (
            <p className="mt-2 text-sm leading-6 text-slate-600" data-testid={`review-q-${row.qid}-why`}>
              <span className="mr-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Why yours attracts</span>
              {row.why_yours_attracts}
            </p>
          )}
          {row.explanation && (
            <p className="mt-2 text-sm leading-6 text-slate-600" data-testid={`review-q-${row.qid}-explain`}>
              <span className="mr-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Explanation</span>
              {row.explanation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
